import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import { NoorBadge } from './NoorLogo';
import { useLanguage, Language } from '../contexts/LanguageContext';
type FooterStrings = {
  brandDesc: string;
  followLabel: string;
  facebookLabel: string;
  youtubeLabel: string;
  serviceHeading: string;
  legalHeading: string;
  howItWorks: string;
  pricing: string;
  faq: string;
  contact: string;
  terms: string;
  privacy: string;
  refund: string;
  accessibility: string;
  requiredNoticeHeading: string;
  requiredNotice: string;
  disclaimerHeading: string;
  disclaimerP1: string;
  disclaimerP2: string;
  disclaimerP3: string;
  rights: string;
};

// Redes sociales de Noor. Para agregar otra red en el futuro, agregue un
// elemento a esta lista (con su enlace, su etiqueta y su dibujo).
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61594938024232';
const YOUTUBE_URL = 'https://www.youtube.com/@noorpassportservices';

// EN/ES finalized. HT/VI = best effort, pending native review.
// requiredNotice* fields added 2026-09-22: verbatim statutory notice text
// required by Fla. Stat. § 501.1391 (ch. 2025-82, s.3), effective 2025-07-01,
// verified against the official Florida Senate statute text and the enrolled
// bill (HB 915). Must stay word-for-word identical to the statute.
// 2026-10-19: brandDesc acortado a una sola línea (el resto era redundante con
// el aviso legal de abajo); agregados los íconos de Facebook y YouTube.
// 2026-10-20: el enlace "Accesibilidad" ya apunta a la página real /accessibility.
const T: Record<Language, FooterStrings> = {
  en: {
    brandDesc: 'Noor Passport Services is a DBA of Noor Metabolico LLC.',
    followLabel: 'Follow us',
    facebookLabel: 'Noor Passport Services on Facebook',
    youtubeLabel: 'Noor Passport Services on YouTube',
    serviceHeading: 'Service',
    legalHeading: 'Legal',
    howItWorks: 'How it Works',
    pricing: 'Pricing',
    faq: 'FAQ',
    contact: 'Contact',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    refund: 'Refund Policy',
    accessibility: 'Accessibility',
    requiredNoticeHeading: 'Required Notice — Florida Statute § 501.1391',
    requiredNotice: 'I AM NOT AN ATTORNEY LICENSED TO PRACTICE LAW AND MAY NOT GIVE LEGAL ADVICE OR ACCEPT FEES FOR LEGAL ADVICE. I AM NOT ACCREDITED TO REPRESENT YOU IN IMMIGRATION MATTERS.',
    disclaimerHeading: 'Legal Disclaimer',
    disclaimerP1: 'Noor Passport Services is not a law firm and does not provide legal advice. Our service is limited to document preparation software.',
    disclaimerP2: 'We are not affiliated with the U.S. Department of State or any other U.S. government agency. Blank government forms are available for free directly from the U.S. Department of State.',
    disclaimerP3: 'Customers are solely responsible for reviewing, signing, and submitting their own application to the appropriate government office. We do not submit applications or interact with the Department of State on your behalf.',
    rights: 'All rights reserved.'
  },
  es: {
    brandDesc: 'Noor Passport Services es un nombre comercial (DBA) de Noor Metabolico LLC.',
    followLabel: 'Síganos',
    facebookLabel: 'Noor Passport Services en Facebook',
    youtubeLabel: 'Noor Passport Services en YouTube',
    serviceHeading: 'Servicio',
    legalHeading: 'Legal',
    howItWorks: 'Cómo funciona',
    pricing: 'Precios',
    faq: 'Preguntas',
    contact: 'Contacto',
    terms: 'Términos del servicio',
    privacy: 'Política de privacidad',
    refund: 'Política de reembolso',
    accessibility: 'Accesibilidad',
    requiredNoticeHeading: 'Aviso Obligatorio — Estatuto de Florida § 501.1391',
    requiredNotice: 'NO SOY ABOGADO CON LICENCIA PARA EJERCER LA ABOGACÍA Y NO PUEDO DAR ASESORÍA LEGAL NI COBRAR HONORARIOS POR ASESORÍA LEGAL. NO ESTOY ACREDITADO PARA REPRESENTARLO EN ASUNTOS DE INMIGRACIÓN.',
    disclaimerHeading: 'Aviso legal',
    disclaimerP1: 'Noor Passport Services no es un bufete de abogados y no brinda asesoría legal. Nuestro servicio se limita a software de preparación de documentos.',
    disclaimerP2: 'No estamos afiliados al Departamento de Estado de EE. UU. ni a ninguna otra agencia del gobierno de EE. UU. Los formularios oficiales en blanco están disponibles gratis directamente en el Departamento de Estado de EE. UU.',
    disclaimerP3: 'Los clientes son los únicos responsables de revisar, firmar y entregar su propia solicitud en la oficina gubernamental correspondiente. Nosotros no entregamos solicitudes ni actuamos ante el Departamento de Estado en su nombre.',
    rights: 'Todos los derechos reservados.'
  },
  ht: {
    brandDesc: 'Noor Passport Services se yon non komèsyal (DBA) pou Noor Metabolico LLC.',
    followLabel: 'Swiv nou',
    facebookLabel: 'Noor Passport Services sou Facebook',
    youtubeLabel: 'Noor Passport Services sou YouTube',
    serviceHeading: 'Sèvis',
    legalHeading: 'Legal',
    howItWorks: 'Kijan li mache',
    pricing: 'Pri',
    faq: 'Kesyon',
    contact: 'Kontak',
    terms: 'Kondisyon sèvis',
    privacy: 'Politik konfidansyalite',
    refund: 'Politik ranbousman',
    accessibility: 'Aksesibilite',
    requiredNoticeHeading: 'Avi Obligatwa — Lwa Florid § 501.1391',
    requiredNotice: 'MWEN PA YON AVOKA KI GEN LISANS POU PRATIKE LWA EPI MWEN PA KA BAY KONSÈY LEGAL OSWA AKSEPTE ONORÈ POU KONSÈY LEGAL. MWEN PA AKREDITE POU REPREZANTE OU NAN ZAFÈ IMIGRASYON.',
    disclaimerHeading: 'Avètisman legal',
    disclaimerP1: 'Noor Passport Services se pa yon kabinè avoka epi li pa bay konsèy legal. Sèvis nou an limite ak lojisyèl preparasyon dokiman.',
    disclaimerP2: 'Nou pa afilye ak Depatman Deta Etazini ni ak okenn lòt ajans gouvènman ameriken. Fòm gouvènman vid yo disponib gratis dirèkteman nan Depatman Deta Etazini.',
    disclaimerP3: 'Se kliyan yo sèl ki responsab pou revize, siyen, epi soumèt pwòp aplikasyon yo nan biwo gouvènman ki apwopriye a. Nou pa soumèt aplikasyon ni nou pa aji devan Depatman Deta a nan non ou.',
    rights: 'Tout dwa rezève.'
  },
  vi: {
    brandDesc: 'Noor Passport Services là tên giao dịch (DBA) của Noor Metabolico LLC.',
    followLabel: 'Theo dõi chúng tôi',
    facebookLabel: 'Noor Passport Services trên Facebook',
    youtubeLabel: 'Noor Passport Services trên YouTube',
    serviceHeading: 'Dịch vụ',
    legalHeading: 'Pháp lý',
    howItWorks: 'Cách hoạt động',
    pricing: 'Bảng giá',
    faq: 'Câu hỏi',
    contact: 'Liên hệ',
    terms: 'Điều khoản dịch vụ',
    privacy: 'Chính sách bảo mật',
    refund: 'Chính sách hoàn tiền',
    accessibility: 'Khả năng tiếp cận',
    requiredNoticeHeading: 'Thông Báo Bắt Buộc — Đạo Luật Florida § 501.1391',
    requiredNotice: 'TÔI KHÔNG PHẢI LÀ LUẬT SƯ CÓ GIẤY PHÉP HÀNH NGHỀ LUẬT VÀ KHÔNG THỂ ĐƯĐA RA TƯ VẤN PHÁP LÝ HOẶC NHẬN PHÍ TƯ VẤN PHÁP LÝ. TÔI KHÔNG ĐƯỢC CÔNG NHẬN ĐỂ ĐẠI DIỆN CHO QUÝ VỊ TRONG CÁC VẤN ĐỀ DI TRÚ.',
    disclaimerHeading: 'Tuyên bố pháp lý',
    disclaimerP1: 'Noor Passport Services không phải là công ty luật và không cung cấp tư vấn pháp lý. Dịch vụ của chúng tôi chỉ giới hạn ở phần mềm chuẩn bị giấy tờ.',
    disclaimerP2: 'Chúng tôi không liên kết với Bộ Ngoại giao Hoa Kỳ hay bất kỳ cơ quan chính phủ Hoa Kỳ nào khác. Các mẫu đơn chính phủ trống có sẵn miễn phí trực tiếp từ Bộ Ngoại giao Hoa Kỳ.',
    disclaimerP3: 'Khách hàng hoàn toàn chịu trách nhiệm xem lại, ký và nộp đơn của chính mình tại cơ quan chính phủ thích hợp. Chúng tôi không nộp đơn hay thay mặt quý vị làm việc với Bộ Ngoại giao.',
    rights: 'Bảo lưu mọi quyền.'
  }
};
function FacebookIcon() {
  return <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" data-id="element-182" data-mp="eeo5fs9ueh4c">
      <path d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.6-1.5h1.6V4.4c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.1H7.6v3h2.7V21h3.2z" data-id="element-183" data-mp="2dnuxlx14jl2j" />
    </svg>;
}
function YouTubeIcon() {
  return <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true" data-id="element-184" data-mp="1hnv5600jhxtj">
      <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2C2 8.8 2 12 2 12s0 3.2.4 4.8a2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8c.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8zM10 15V9l5.2 3L10 15z" data-id="element-185" data-mp="17w8kze684oen" />
    </svg>;
}
export function Footer() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const navItems = [{
    to: '/how-it-works',
    label: t.howItWorks
  }, {
    to: '/pricing',
    label: t.pricing
  }, {
    to: '/faq',
    label: t.faq
  }, {
    to: '/contact',
    label: t.contact
  }];
  // Rutas reales de las páginas legales y de accesibilidad.
  const legalLinks: {
    to: string;
    label: string;
  }[] = [{
    to: '/terms-of-service',
    label: t.terms
  }, {
    to: '/privacy-policy',
    label: t.privacy
  }, {
    to: '/refund-policy',
    label: t.refund
  }, {
    to: '/accessibility',
    label: t.accessibility
  }];
  const socials: {
    href: string;
    label: string;
    icon: React.ReactNode;
  }[] = [{
    href: FACEBOOK_URL,
    label: t.facebookLabel,
    icon: <FacebookIcon data-id="element-186" data-mp="mdq7vkqp7o2i" />
  }, {
    href: YOUTUBE_URL,
    label: t.youtubeLabel,
    icon: <YouTubeIcon data-id="element-187" data-mp="hm84yr547dew" />
  }];
  return <footer className="bg-brand-footerBg text-white pt-20 pb-10 relative z-10" data-id="element-188" data-mp="hl6izrmk96ie">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-189" data-mp="vcjzewp69o97">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-190" data-mp="qea4mn7d0po4">
          {/* Brand */}
          <div className="lg:col-span-2" data-id="element-191" data-mp="15b98q13060du">
            <div className="flex items-center gap-2 mb-6" data-id="element-192" data-mp="15a7yfytt9hx6">
              <NoorBadge size={40} data-id="element-193" data-mp="10cpmdzkhymky" />
              <span className="font-heading font-bold text-2xl tracking-tight text-white" data-id="element-194" data-mp="10dr6suneklfc">
                Noor
              </span>
            </div>
            <p className="text-gray-300 text-base leading-relaxed mb-5 max-w-md" data-id="element-195" data-mp="323sykta1c97m">
              {t.brandDesc}
            </p>
            <div className="flex gap-3 text-sm text-gray-400 mb-6" data-id="element-196" data-mp="iuid2jvae04n">
              <a href="mailto:info@noorpassportservices.com" className="text-gray-300 hover:text-white transition-colors underline underline-offset-2" data-id="element-197" data-mp="dv5ex2yyt6mr">
                info@noorpassportservices.com
              </a>
            </div>
            <div className="flex items-center gap-3" data-id="element-198" data-mp="3z2ga73bnq4k">
              <span className="text-sm text-gray-400 mr-1" data-id="element-199" data-mp="11jonljlxflfd">{t.followLabel}</span>
              {socials.map(s => <a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label} className="w-11 h-11 rounded-full border border-brand-accent/60 text-brand-accent hover:bg-brand-accent hover:text-brand-footerBg transition-colors flex items-center justify-center" data-id="element-200" data-mp="wm5ain0ytep0">
                  {s.icon}
                </a>)}
            </div>
          </div>

          {/* Service */}
          <div data-id="element-201" data-mp="1rtyvjzrn5dng">
            <h4 className="font-heading font-semibold text-lg mb-6 text-white" data-id="element-202" data-mp="1rvia9lxnfgn4">
              {t.serviceHeading}
            </h4>
            <ul className="space-y-4" data-id="element-203" data-mp="1wvwknamq32ob">
              {navItems.map(item => <li key={item.to} data-id="element-204" data-mp="21uhlnc9stqdt">
                  <Link to={item.to} className="text-gray-300 hover:text-white transition-colors text-sm underline underline-offset-2" data-id="element-205" data-mp="26rzmsghkr1b9">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Legal */}
          <div data-id="element-206" data-mp="2kicvwm67cm26">
            <h4 className="font-heading font-semibold text-lg mb-6 text-white" data-id="element-207" data-mp="2kiwe83zrlcgq">
              {t.legalHeading}
            </h4>
            <ul className="space-y-4" data-id="element-208" data-mp="2pgo3srcxsmhr">
              {legalLinks.map(item => <li key={item.to} data-id="element-209" data-mp="2ufhhokw4yfig">
                  <Link to={item.to} className="text-gray-300 hover:text-white transition-colors text-sm underline underline-offset-2" data-id="element-210" data-mp="24t6lwydj8l76">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Required statutory notice — Fla. Stat. § 501.1391. Must be
            conspicuous and word-for-word identical to the statute text in
            every language the service is offered in. Kept visually distinct
            (accent border) from the general disclaimer below it. */}
        <div className="p-6 md:p-8 bg-white/[0.04] border-2 border-brand-accent/60 rounded-2xl mb-6" data-id="element-211" data-mp="2xh2js6eu8j5j">
          <div className="flex items-start gap-3 mb-3" data-id="element-212" data-mp="2skc43k6y3pkn">
            <ShieldAlert className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-213" data-mp="1342u1kgqu8kj" />
            <h4 className="font-heading font-semibold text-lg text-white" data-id="element-214" data-mp="135xih8r84itd">
              {t.requiredNoticeHeading}
            </h4>
          </div>
          <p className="text-sm md:text-base font-semibold tracking-wide text-gray-200 leading-relaxed uppercase" data-id="element-215" data-mp="39tec7duhw8wj">
            {t.requiredNotice}
          </p>
        </div>

        {/* Prominent Legal Disclaimer */}
        <div className="p-6 md:p-8 bg-white/[0.04] border border-white/10 rounded-2xl mb-10" data-id="element-216" data-mp="3020jbox1u7cd">
          <div className="flex items-start gap-3 mb-4" data-id="element-217" data-mp="350bqtszvg5rf">
            <AlertCircle className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-218" data-mp="2r23mh00ia268" />
            <h4 className="font-heading font-semibold text-lg text-white" data-id="element-219" data-mp="2v3gungr2mhgb">
              {t.disclaimerHeading}
            </h4>
          </div>
          <div className="space-y-3 text-sm md:text-base text-gray-300 leading-relaxed" data-id="element-220" data-mp="2h4pjo37i9bwe">
            <p data-id="element-221" data-mp="2m1z8phqzfljp">{t.disclaimerP1}</p>
            <p data-id="element-222" data-mp="2m30vgjhd1wh9">{t.disclaimerP2}</p>
            <p data-id="element-223" data-mp="fcb2j6cscbh1">{t.disclaimerP3}</p>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-224" data-mp="fd41vm9ezyp5">
          <p className="text-gray-500 text-sm" data-id="element-225" data-mp="k91lc1fz5l65">
            © {new Date().getFullYear()} Noor Passport Services DBA Noor
            Metabolico LLC. {t.rights}
          </p>
          <div className="flex gap-4 text-sm text-gray-500" data-id="element-226" data-mp="2bfz59jv1ihw4">
            <span data-id="element-227" data-mp="27d2qy5cj97xz">English</span>
            <span aria-hidden="true" data-id="element-228" data-mp="27cavcuixra7p">·</span>
            <span data-id="element-229" data-mp="22f189lj0ran1">Español</span>
            <span aria-hidden="true" data-id="element-230" data-mp="22dptkqo1cih5">·</span>
            <span data-id="element-231" data-mp="22gvlseryo0h3">Kreyòl</span>
            <span aria-hidden="true" data-id="element-232" data-mp="1xg7stnk699k8">·</span>
            <span data-id="element-233" data-mp="1xh93vs16tw1y">Tiếng Việt</span>
          </div>
        </div>
      </div>
    </footer>;
}